/* import __COLOCATED_TEMPLATE__ from './test-result.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { task } from 'ember-concurrency-decorators';
import { post } from 'embercom/lib/ajax';
import { isBlank } from '@ember/utils';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type ApiRequestConfig from 'embercom/models/workflow-connector/customer-data-verification/api-request-config';

interface Args {
  apiRequestConfig: ApiRequestConfig;
  templatedParameters: any;
  clearTemplatedParameter: () => void;
  saveTemplatedParameter: (requestParameter: any, requestValue: any) => void;
}

export default class TestResult extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: any;
  @service declare attributeService: any;
  @service intercomEventService: any;
  @service declare store: Store;

  @tracked declare bannerText: string;
  @tracked declare bannerType: 'confirmation' | 'error';
  @tracked declare testResponse: any;
  @tracked declare responseBodyToDisplay: any;
  @tracked disableTestButton = false;
  @tracked attributeNames: any = {};

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.getAttributeNames();
    this.requestParameters();
    this.getValidTest();
    this.setDisableTestButton();
  }

  getAttributeNames() {
    this.allAttributes.forEach(
      (attribute: any) => (this.attributeNames[attribute.identifier] = attribute.name),
    );
    this.resolver.eventAttributes.forEach(
      (attribute: any) => (this.attributeNames[attribute.templatable_identifier] = attribute.name),
    );
  }

  requestParameters() {
    let currentParameters = this.args.templatedParameters;
    this.args.clearTemplatedParameter();

    Array.from(this.args.apiRequestConfig?.attributes).map((attribute) =>
      this.args.saveTemplatedParameter(attribute[1], currentParameters[attribute[1]] || ''),
    );
  }

  getValidTest() {
    if (this.args.apiRequestConfig.testIsValid) {
      let body = this.args.apiRequestConfig.testResult.actionResponse.body;
      let status = this.args.apiRequestConfig.testResult.actionResponse.status.toString();
      let code = this.args.apiRequestConfig.testResult.actionResponse.returnCode;
      let success = this.args.apiRequestConfig.testResult.actionResponse.success;

      this.responseBodyToDisplay = JSON.stringify(body, null, 2);
      this._setBannerSuccessMessage(status, code, success);
    } else if (this.args.apiRequestConfig.isJsonInvalid) {
      this._setInvalidJsonMessage();
    }
  }

  setDisableTestButton() {
    this.disableTestButton = Object.values(this.args.templatedParameters).some(isBlank);
  }

  validateRequestBody() {
    if (['get', 'delete'].includes(this.args.apiRequestConfig.httpMethod)) {
      this.args.apiRequestConfig.body = null;
    }
  }

  get resolver(): AttributeInfoResolver {
    return new AttributeInfoResolver({
      attributes: this.attributeService.allAttributes,
      includeAppAttributes: true,
    });
  }

  get allAttributes() {
    return [...this.attributeService.allAttributes, ...this.resolver.manualAppAttributes];
  }

  get showTestValuesHeaders() {
    return Object.keys(this.args.templatedParameters).length;
  }

  @action
  setParameterValue(requestParameter: any, event: any) {
    this.args.saveTemplatedParameter(requestParameter, event.target.value);
    this.setDisableTestButton();
  }

  @task({ drop: true })
  *testRequest(): TaskGenerator<void> {
    this.validateRequestBody();
    let params = {
      app_id: this.appService.app.id,
      // TODO: This is being commented because we do not have an API yet which tests this request and we don't have a workflow action id here.
      //   workflow_action: this.args.workflowAction,
      templated_values: this.args.templatedParameters,
    };

    try {
      // TODO: This url does not exist yet.
      let testResponse = yield post(`/ember/workflow_connector/actions/test`, params);
      this.testResponse = testResponse.test_result;
      this.responseBodyToDisplay = this.getResponseBody(this.testResponse.action_response);

      let normalizedTestResult = (
        this.store.normalize(
          'workflow-connector/customer-data-verification/test-result',
          this.testResponse,
        ) as any
      ).data.attributes;
      let testResult = this.store.createFragment(
        'workflow-connector/customer-data-verification/test-result',
        normalizedTestResult,
      );
      this.args.apiRequestConfig.testResult = testResult;

      let responseFields = testResponse.response_fields.map((responseField: any) => {
        let normalizedResponseFields = (
          this.store.normalize(
            'workflow-connector/customer-data-verification/response-field',
            responseField,
          ) as any
        ).data.attributes;

        return this.store.createFragment(
          'workflow-connector/customer-data-verification/response-field',
          normalizedResponseFields,
        );
      });

      this.args.apiRequestConfig.responseFields = responseFields;

      this._setBannerParams();
    } catch (exception) {
      this._setBannerOnException(exception);
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'test_request',
      api_request_config_id: this.args.apiRequestConfig.id,
    });
  }

  get isTestResultRunning() {
    return taskFor(this.testRequest).isRunning;
  }

  _setBannerParams() {
    if (this.testResponse.action_response.success) {
      if (this.args.apiRequestConfig.isJsonInvalid) {
        this._setInvalidJsonMessage();
        return;
      }

      this._setBannerSuccessMessage(
        this.testResponse.action_response.status.toString(),
        this.testResponse.action_response.return_code,
        this.testResponse.action_response.success,
      );
      this.setTestedRequest();
    } else {
      this.bannerText = this._getErrorMessage(this.testResponse.action_response);
      this.bannerType = 'error';
    }
  }

  _setBannerSuccessMessage(status: string, code: string, success: boolean) {
    let isSuccessHttpStatus = status.startsWith('2');
    if (code.toLowerCase() === 'ok') {
      code = code.toUpperCase();
    }

    if (success && isSuccessHttpStatus) {
      this.bannerText = this.intl.t('workflow-connector.builder.body.testing.success-response', {
        status: `${status} ${code}`,
        htmlSafe: true,
      });
    } else {
      this.bannerText = `${status} ${code}`;
    }

    this.bannerType = 'confirmation';
  }

  _setInvalidJsonMessage() {
    this.bannerText = this.intl.t('workflow-connector.builder.body.testing.invalid-json');
    this.bannerType = 'error';
    this.responseBodyToDisplay = this.args.apiRequestConfig.testResult?.actionResponse?.rawBody;
  }

  _getErrorMessage(action_response: any) {
    return (
      action_response.human_readable_error ||
      action_response.body?.errors ||
      this.intl.t('workflow-connector.builder.body.testing.error-message')
    );
  }

  _setBannerOnException(exception: any) {
    this.bannerType = 'error';
    this.bannerText = exception.errorThrown;
  }

  getResponseBody(action_response: any) {
    return action_response.body !== null
      ? JSON.stringify(this.testResponse.action_response.body, null, 2)
      : action_response.raw_body;
  }

  setTestedRequest() {
    this.args.apiRequestConfig.testResult.testedAction = {};
    this.args.apiRequestConfig.testResult.testedAction.httpMethod =
      this.args.apiRequestConfig.httpMethod;
    this.args.apiRequestConfig.testResult.testedAction.url = this.args.apiRequestConfig.url;
    this.args.apiRequestConfig.testResult.testedAction.headers = this.args.apiRequestConfig.headers;
    this.args.apiRequestConfig.testResult.testedAction.body = this.args.apiRequestConfig.body;
    this.args.apiRequestConfig.testResult.testedAction.tokenId = this.args.apiRequestConfig.tokenId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::CustomerDataVerification::Sections::Testing::TestResult': typeof TestResult;
    'workflow-connector/builder/body/customer-data-verification/sections/testing/test-result': typeof TestResult;
  }
}
